import React from 'react';
import { buildGroupPeriodComboSuggests } from '../../constants/groupPeriods';
import { formatPeriodDateRange } from '../../utils/formatPeriodDates';

function periodDisplayLabel(period) {
  if (!period) return 'School year';
  const title = period.title || period.code || 'Period';
  const range = formatPeriodDateRange(period.start_date, period.end_date);
  return range ? `${title} (${range})` : title;
}

/**
 * Nested group: choose same period as attach-under (recommended) or a different period.
 * When "different period" is selected, children render the lasting / varying fork.
 */
const GroupsNestedTeamPeriodChoice = ({
  parentPeriod = null,
  parentTeamName = '',
  choice = 'parent',
  onChoiceChange,
  setFormData,
  organizationName = '',
  periodDescriptionContext = null,
  subjectName = 'Group',
  onShowCreatePeriodChange,
  disabled = false,
  children = null
}) => {
  const parentLabel = periodDisplayLabel(parentPeriod);
  const parentTeam = String(
    parentTeamName || periodDescriptionContext?.parentTeamName || 'parent'
  ).trim();

  const selectParentPeriod = () => {
    if (!parentPeriod?.id) return;
    const periodId = String(parentPeriod.id);
    setFormData((prev) => ({
      ...prev,
      periodId,
      ...buildGroupPeriodComboSuggests(
        prev.name || subjectName,
        parentPeriod,
        organizationName,
        periodDescriptionContext
      )
    }));
  };

  return (
    <div className="groups-nested-period-choices">
      <div
        className="groups-nested-period-mode"
        role="radiogroup"
        aria-label="New group's time period"
      >
        <button
          type="button"
          role="radio"
          aria-checked={choice === 'parent'}
          className={`groups-nested-period-card${choice === 'parent' ? ' is-selected' : ''}${
            !parentPeriod?.id ? ' is-disabled' : ''
          }`}
          onClick={() => {
            if (!parentPeriod?.id || disabled) return;
            onChoiceChange?.('parent');
            onShowCreatePeriodChange?.(false);
            selectParentPeriod();
          }}
          disabled={disabled || !parentPeriod?.id}
        >
          <span className="groups-nested-period-card-title">Use parent period</span>
          <span className="groups-nested-period-card-note">
            Same period as <strong>{parentTeam}</strong>
            {parentPeriod?.id ? (
              <>
                {' '}
                · <span className="groups-nested-period-card-period-inline">{parentLabel}</span>
              </>
            ) : null}
            .
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={choice === 'own'}
          className={`groups-nested-period-card groups-nested-period-card--own${
            choice === 'own' ? ' is-selected' : ''
          }`}
          onClick={() => {
            if (disabled) return;
            onChoiceChange?.('own');
            setFormData((prev) => ({
              ...prev,
              periodId: '',
              groupPeriodTitle: '',
              groupPeriodDescription: ''
            }));
            onShowCreatePeriodChange?.(false);
          }}
          disabled={disabled}
        >
          <span className="groups-nested-period-card-title">Use a different period</span>
          <span className="groups-nested-period-card-note">
            Only if this group needs its own lasting or term calendar.
          </span>
        </button>
      </div>

      {choice === 'own' ? (
        <div className="groups-nested-period-own-panel">{children}</div>
      ) : null}
    </div>
  );
};

export default GroupsNestedTeamPeriodChoice;
